import type { ButtonHTMLAttributes, ReactNode } from 'react';

// UX-48 : `ghost` ajouté — premier besoin réel d'un bouton SANS fond ni bordure
// (action secondaire dans une ligne de tableau, ManageClasses.tsx) ; `secondary`
// reste bordé/fond blanc, visuellement trop lourd pour ce contexte. Comme `size`
// (UX-12), traité comme un VRAI variant plutôt qu'un `className` de substitution —
// même risque de conflit de spécificité Tailwind déjà écarté par ce précédent.
// UX-65 : `amber` ajouté — 5e variante déjà documentée par le Design System
// mais jamais implémentée. Triplet ambre réutilisé TEL QUEL depuis UX-63/UX-64
// (bg-amber-100/text-amber-800/accent amber-600), jamais une autre valeur.
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'amber';
// UX-12 : `size` ajouté (premier besoin réel, `Exporter` sur StudentsOverview.tsx) —
// PAS géré via un `className` de substitution passé par l'appelant : les classes
// Tailwind du `className` de l'appelant s'AJOUTENT après celles du composant dans
// l'attribut `class` du DOM, elles ne les REMPLACENT PAS par spécificité CSS (les
// deux définitions de padding/taille de police coexisteraient, résultat dépendant
// de l'ordre d'apparition dans la feuille de styles générée par Tailwind — pas
// fiable). `default` reproduit EXACTEMENT `BASE_STYLES` d'avant ce ticket — aucun
// changement de rendu pour les dizaines d'appelants existants qui ne passent pas
// `size`.
export type ButtonSize = 'default' | 'small';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

// FND-20 : `ss-button--${variant}` PRÉSERVÉ tel quel (Button.test.tsx —
// `toHaveClass('ss-button--danger')`) : marqueur sémantique sur lequel le test
// s'appuie, les classes Tailwind s'AJOUTENT à côté, elles ne le remplacent
// pas. Couleurs `primary` issues du thème partagé (packages/ui/src/theme.css,
// palette `brand`) ; `secondary`/`danger` réutilisent les palettes
// neutre/rouge par défaut de Tailwind (pas besoin d'un token dédié).
// UX-48 : `shadow-sm` DÉPLACÉ hors de `BASE_STYLES` vers chacun des 3 variants
// existants (comportement STRICTEMENT inchangé pour eux) — jamais laissé ici en
// même temps qu'un futur `shadow-none` posé sur `ghost` dans `VARIANT_STYLES` :
// deux classes de MÊME propriété CSS coexistant dans l'attribut `class` ne se
// remplacent pas de façon fiable par ordre d'apparition (même piège déjà
// documenté par le commentaire `size`/UX-12 ci-dessus, pour `px-*`/`py-*`).
// UX-66 : `disabled:opacity-60` → `disabled:opacity-40` — le doc Design System
// documente 0.4 comme valeur unique d'état désactivé pour tout le produit
// (vérifié : `opacity-60` ici, `opacity-50` sur 10 boutons faits main,
// `opacity-60` sur 2 liens texte — 3 valeurs, aucune à 0.4, avant ce ticket).
const BASE_STYLES =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed ' +
  'disabled:opacity-40';

const SIZE_STYLES: Record<ButtonSize, string> = {
  default: 'px-4 py-2 text-sm',
  small: 'ss-button--size-small px-2.5 py-1 text-xs',
};

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary: 'ss-button--primary shadow-sm bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500',
  secondary:
    'ss-button--secondary shadow-sm border border-border-strong bg-white text-text-secondary hover:bg-surface-muted focus-visible:ring-gray-400',
  danger: 'ss-button--danger shadow-sm bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
  // Pas de `shadow-sm`/bordure/fond — action secondaire discrète dans une ligne de
  // tableau (ManageClasses.tsx, UX-48), jamais un appel à l'action principal.
  ghost: 'ss-button--ghost bg-transparent text-text-tertiary hover:bg-surface-raised focus-visible:ring-gray-400',
  // UX-65 : triplet ambre déjà validé (UX-63/UX-64) — bg-amber-100/text-amber-800,
  // bordure/accent amber-600, jamais une autre valeur.
  amber:
    'ss-button--amber shadow-sm border border-amber-600 bg-amber-100 text-amber-800 hover:bg-amber-200 focus-visible:ring-amber-500',
};

export function Button({ variant = 'primary', size = 'default', className, children, ...rest }: ButtonProps) {
  const classes = ['ss-button', BASE_STYLES, SIZE_STYLES[size], VARIANT_STYLES[variant], className]
    .filter(Boolean)
    .join(' ');
  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}
